import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, ChevronLeft, RefreshCw } from 'lucide-react';
import type { LearningPath, NodeContent } from '@shared/types/learning-path';
import { regenerateNode, loadNodeContent } from '@shared/services/learningPath';
import { NodeContentRenderer } from './NodeContentRenderer';
import { ProgressTracker } from './ProgressTracker';
import { Button } from '../ui/Button';
import { THICK_TILE_STYLE } from '../dashboard/tileStyles';

interface ConceptMapLayoutProps {
  path: LearningPath;
  nodeContents: Map<string, NodeContent>;
  currentNodeId: string | null;
  onNavigate: (nodeId: string) => void;
  onNodeContentUpdated?: (nodeId: string, content: NodeContent) => void;
}

const SIDEBAR_COLLAPSED_KEY = 'cogniprint:learning-sidebar-collapsed';

function loadCollapsedPref(): boolean {
  if (typeof localStorage === 'undefined') return false;
  try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1'; } catch { return false; }
}

export function ConceptMapLayout({ path, nodeContents, currentNodeId, onNavigate, onNodeContentUpdated }: ConceptMapLayoutProps) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(loadCollapsedPref);
  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  }, []);
  const [regenerating, setRegenerating] = useState<string | null>(null);
  // Progress estimate while a regenerate is in flight. Server work is
  // roughly linear (Claude content gen + Haiku validation + SVG render),
  // typically ~38s. We tick toward 95% with a cubic-ease-out, then snap
  // to 100% when the server returns. No real per-step signal is exposed
  // — this is an honest-feeling estimate, not a measured percentage.
  const [regeneratePct, setRegeneratePct] = useState(0);

  useEffect(() => {
    if (!regenerating) {
      setRegeneratePct(0);
      return;
    }
    const start = Date.now();
    const expectedMs = 38000;
    const id = window.setInterval(() => {
      const t = Math.min(1, (Date.now() - start) / expectedMs);
      // 1 - (1-t)^3 — fast at start, settles toward 95% near the end
      const eased = Math.min(0.95, 1 - Math.pow(1 - t, 3));
      setRegeneratePct(Math.round(eased * 100));
    }, 250);
    return () => window.clearInterval(id);
  }, [regenerating]);
  const completedSet = new Set(path.progress.completedNodes);

  const currentIndex = path.nodes.findIndex((n) => n.nodeId === currentNodeId);
  const currentNode = currentIndex >= 0 ? path.nodes[currentIndex] : path.nodes[0];
  const currentContent = currentNode ? nodeContents.get(currentNode.nodeId) : null;
  const prevNode = currentIndex > 0 ? path.nodes[currentIndex - 1] : null;
  const nextNode = currentIndex < path.nodes.length - 1 ? path.nodes[currentIndex + 1] : null;

  const handleNodeClick = useCallback((nodeId: string) => {
    onNavigate(nodeId);
  }, [onNavigate]);

  return (
    <div className="flex min-h-screen bg-dark-bg">
      {/* Sidebar (desktop) — collapsible to an icon rail */}
      <div className="hidden lg:block relative shrink-0 h-screen sticky top-0">
        <ProgressTracker
          path={path}
          currentNodeId={currentNodeId}
          onNavigate={handleNodeClick}
          variant="sidebar"
          nodeContents={nodeContents}
          collapsed={sidebarCollapsed}
        />
        {/* Collapse toggle — circular button centered on the sidebar's right
            edge line, with gold outlines to match the progress ring. */}
        <button
          type="button"
          onClick={toggleSidebar}
          aria-label={sidebarCollapsed ? 'Expand section list' : 'Collapse section list'}
          aria-expanded={!sidebarCollapsed}
          title={sidebarCollapsed ? 'Expand section list' : 'Collapse section list'}
          className="group absolute top-6 right-0 translate-x-1/2 z-20 w-7 h-7 rounded-full
            bg-dark-elevated border border-gold-500 shadow-[0_0_12px_rgba(240,165,0,0.25)]
            flex items-center justify-center cursor-pointer
            text-gold-500 hover:text-dark-bg hover:bg-gold-500 hover:border-gold-400
            hover:shadow-glow-gold
            transition-all duration-150"
        >
          <ChevronLeft
            size={14}
            strokeWidth={2.5}
            className={`transition-transform duration-200 ${sidebarCollapsed ? 'rotate-180' : ''}`}
          />
        </button>
      </div>

      {/* Progress bar (mobile) */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-30">
        <ProgressTracker path={path} currentNodeId={currentNodeId} onNavigate={handleNodeClick} variant="bar" />
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top bar */}
        <div className="shrink-0 flex items-center justify-between px-6 py-3 border-b border-dark-border">
          <Link
            to="/dashboard"
            className="flex items-center gap-1.5 text-sm text-content hover:text-white transition-colors font-body"
          >
            <ArrowLeft size={14} />
            Dashboard
          </Link>

          {/* Concept Map SVG — compact */}
          <div className="hidden md:block flex-1 max-w-md mx-8">
            <svg viewBox={`0 0 ${Math.min(path.nodes.length * 30, 800)} 40`} className="w-full h-8">
              {path.nodes.map((node, i) => {
                const x = 10 + i * ((Math.min(path.nodes.length * 30, 800) - 20) / Math.max(path.nodes.length - 1, 1));
                const isCompleted = completedSet.has(node.nodeId);
                const isCurrent = node.nodeId === currentNodeId;

                return (
                  <g key={node.nodeId}>
                    {i < path.nodes.length - 1 && (
                      <line
                        x1={x} y1={20}
                        x2={10 + (i + 1) * ((Math.min(path.nodes.length * 30, 800) - 20) / Math.max(path.nodes.length - 1, 1))} y2={20}
                        stroke={isCompleted ? '#6C5CE7' : '#2A2F4F'}
                        strokeWidth={1}
                      />
                    )}
                    <circle
                      cx={x} cy={20}
                      r={isCurrent ? 6 : 4}
                      fill={isCompleted ? '#6C5CE7' : isCurrent ? '#0B0E1A' : '#181D35'}
                      stroke={isCurrent ? '#F0A500' : isCompleted ? '#6C5CE7' : '#2A2F4F'}
                      strokeWidth={isCurrent ? 2 : 1}
                      className="cursor-pointer"
                      onClick={() => handleNodeClick(node.nodeId)}
                    />
                  </g>
                );
              })}
            </svg>
          </div>

          <p className="text-xs text-content-secondary font-body">
            {currentIndex + 1} / {path.nodes.length}
          </p>
        </div>

        {/* Single section content */}
        <div className="flex-1 overflow-y-auto">
          <AnimatePresence mode="wait">
            {currentNode && (
              <motion.div
                key={currentNode.nodeId}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.2 }}
              >
                {/* Section header */}
                <div className="px-6 sm:px-10 lg:px-16 py-5 border-b border-dark-border">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                      {completedSet.has(currentNode.nodeId) && <span className="text-success text-sm shrink-0">✓</span>}
                      <div className="min-w-0">
                        <h2 className="font-heading font-bold text-lg text-content truncate">
                          {currentContent?.content?.title ?? `Section ${currentIndex + 1}`}
                        </h2>
                        <p className="text-xs text-content-secondary font-body mt-0.5">
                          {currentNode.estimatedDuration}m · {currentNode.nodeType} · {currentNode.contentFormat}
                        </p>
                      </div>
                    </div>
                    <div className="shrink-0 flex items-center gap-3">
                      {regenerating === currentNode.nodeId && (
                        <span
                          aria-live="polite"
                          className="font-mono text-sm text-gold-400 tabular-nums"
                        >
                          {regeneratePct}%
                        </span>
                      )}
                    <button
                      type="button"
                      disabled={regenerating === currentNode.nodeId}
                      onClick={async () => {
                        setRegenerating(currentNode.nodeId);
                        try {
                          await regenerateNode(path.pathId, currentNode.nodeId);
                          // Snap to 100% briefly so the user sees the
                          // completion before the indicator hides.
                          setRegeneratePct(100);
                          const content = await loadNodeContent(path.pathId, currentNode.nodeId);
                          if (content) onNodeContentUpdated?.(currentNode.nodeId, content);
                          await new Promise((r) => window.setTimeout(r, 350));
                        } catch (err) {
                          console.error('Regeneration failed:', err);
                        } finally {
                          setRegenerating(null);
                        }
                      }}
                      style={THICK_TILE_STYLE}
                      className="relative overflow-hidden inline-flex items-center gap-2 rounded-full border-2 border-gold-500/60 hover:border-gold-500/80 text-white px-4 py-2 text-sm font-body font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
                    >
                      {regenerating === currentNode.nodeId ? (
                        <>
                          <motion.span
                            className="w-3.5 h-3.5 border-2 border-gold-500 border-t-transparent rounded-full inline-block"
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                          />
                          Regenerating…
                        </>
                      ) : (
                        <>
                          <RefreshCw size={14} className="text-gold-400" />
                          Regenerate
                        </>
                      )}
                    </button>
                    </div>
                  </div>
                </div>

                {/* Content — white background */}
                {currentContent ? (
                  <div className="bg-white px-6 sm:px-10 lg:px-16 py-8 learning-content-light min-h-[50vh]">
                    <NodeContentRenderer
                      nodeContent={currentContent}
                      node={currentNode}
                      pathId={path.pathId}
                    />
                  </div>
                ) : (
                  <div className="px-6 sm:px-10 lg:px-16 py-16 text-center">
                    {regenerating === currentNode.nodeId ? (
                      <div className="flex flex-col items-center gap-3">
                        <motion.div
                          className="w-8 h-8 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full"
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        />
                        <p className="text-content-secondary font-body text-sm">Generating this section...</p>
                        <p className="text-xs text-content-muted font-body">Usually takes 15–30 seconds</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-4">
                        <img src="/WavingCog.png" alt="Professor Cogni" className="w-16 h-16 object-contain opacity-60" />
                        <p className="text-content-secondary font-body text-sm">This section wasn't generated yet.</p>
                        <Button
                          size="sm"
                          onClick={async () => {
                            setRegenerating(currentNode.nodeId);
                            try {
                              await regenerateNode(path.pathId, currentNode.nodeId);
                              const content = await loadNodeContent(path.pathId, currentNode.nodeId);
                              if (content) {
                                onNodeContentUpdated?.(currentNode.nodeId, content);
                              }
                            } catch (err) {
                              console.error('Regeneration failed:', err);
                            } finally {
                              setRegenerating(null);
                            }
                          }}
                        >
                          <RefreshCw size={14} />
                          Generate This Section
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Bottom navigation */}
        <div className="shrink-0 border-t border-dark-border bg-dark-bg px-6 sm:px-10 lg:px-16 py-4">
          <div className="flex items-center justify-between">
            {prevNode ? (
              <button
                onClick={() => handleNodeClick(prevNode.nodeId)}
                className="flex items-center gap-2 text-sm text-content-secondary hover:text-content font-body transition-colors cursor-pointer"
              >
                <ArrowLeft size={16} />
                Previous
              </button>
            ) : <div />}

            {nextNode ? (
              <Button onClick={() => handleNodeClick(nextNode.nodeId)} size="sm">
                Next <ArrowRight size={14} />
              </Button>
            ) : (
              <Button variant="gold" size="sm" onClick={() => handleNodeClick(path.nodes[0].nodeId)}>
                Complete
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
